import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Store, Mail, Lock, Loader2, Globe, Check, X, Eye, EyeOff, ArrowLeft, ArrowRight, TrendingUp, Zap, Shield, BarChart3, Gift, Briefcase, User, Users, Wallet, Calendar, Heart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
// PlatformSelector removed in professional-only version
type PlatformType = "shopify" | "woocommerce" | "standalone" | "other";
import { setBypassBlocker } from "@/hooks/useNavigationBlocker";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

type UserType = "business" | "professional" | "customer";

const SESSION_KEY = "shakatah_customer_session";

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function UnifiedLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, signIn, signUp, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Check if this is a Shopify-specific flow (from app install)
  const isShopifyMode = searchParams.get("platform") === "shopify";
  
  // Check for Shopify install context early (needed for initial user type)
  const hasShopifyInstallContext = 
    isShopifyMode || 
    searchParams.get("shop") ||
    searchParams.get("pending_token") ||
    sessionStorage.getItem("pending_shopify_shop");

  // Get initial tab from URL or default to business
  const getInitialUserType = (): UserType => {
    return "professional";
  };
  
  const [userType, setUserType] = useState<UserType>(getInitialUserType());

  // Redirect authenticated users away from login page
  // Only redirect if not currently in a loading state (prevents race with login handler)
  useEffect(() => {
    const handleLoggedInRedirect = async () => {
      if (!authLoading && !loading && user) {
        // Check for pending Shopify installation - need to handle this flow even for logged-in users
        const storedShop = sessionStorage.getItem("pending_shopify_shop");
        const storedToken = sessionStorage.getItem("pending_shopify_token");
        const storedShopName = sessionStorage.getItem("pending_shopify_name");
        
        if (storedShop && storedToken) {
          // Logged-in user with pending Shopify data - need to link/create merchant
          try {
            const { data: merchantUser } = await supabase
              .from("merchant_users")
              .select("merchant_id")
              .eq("user_id", user.id)
              .maybeSingle();
            
            if (merchantUser?.merchant_id) {
              // Update existing merchant with Shopify credentials
              await supabase
                .from("merchants")
                .update({
                  shopify_store_domain: storedShop,
                  shopify_access_token: storedToken,
                  store_name: storedShopName || storedShop.replace(".myshopify.com", ""),
                  platform_type: "shopify",
                })
                .eq("id", merchantUser.merchant_id);
              
              toast.success("Shopify store connected successfully!");
            } else {
              // User has no merchant - use SECURITY DEFINER function
              const { error: merchantError } = await supabase
                .rpc('create_merchant_with_user', {
                  p_user_id: user.id,
                  p_store_domain: storedShop,
                  p_store_name: storedShopName || storedShop.replace(".myshopify.com", ""),
                  p_platform_type: "shopify",
                  p_access_token: storedToken,
                  p_widget_installed: true,
                });

              if (!merchantError) {
                toast.success("Shopify store connected successfully!");
              }
            }
            
            // Clear pending data and redirect to merchant onboarding
            sessionStorage.removeItem("pending_shopify_shop");
            sessionStorage.removeItem("pending_shopify_token");
            sessionStorage.removeItem("pending_shopify_name");
            
            setBypassBlocker(true);
            navigate("/merchant/onboarding", { replace: true });
            setTimeout(() => setBypassBlocker(false), 100);
            return;
          } catch (err) {
            console.error("Error handling Shopify for logged-in user:", err);
          }
        }
        
        // Normal redirect for logged-in users without pending Shopify data
        setBypassBlocker(true);
        const redirectUrl = searchParams.get("redirect") || "/merchant-dashboard";
        navigate(redirectUrl, { replace: true });
        setTimeout(() => setBypassBlocker(false), 100);
      }
    };
    
    handleLoggedInRedirect();
  }, [user, authLoading, loading, navigate, searchParams]);
  
  // Check for Shopify install context (from OAuth callback)
  const pendingShopifyShop = sessionStorage.getItem("pending_shopify_shop") || searchParams.get("shop");
  const pendingShopifyToken = searchParams.get("pending_token");
  const pendingShopName = searchParams.get("shop_name");
  const pendingShopEmail = sessionStorage.getItem("pending_shopify_email") || searchParams.get("shop_email");
  const redirectUrl = searchParams.get("redirect") || "";
  const isShopifyInstall = pendingShopifyShop || redirectUrl.includes("shop=") || isShopifyMode || !!pendingShopifyToken;
  
  // Auto-trigger OAuth for Shopify installs that don't have a pending token yet
  // This handles when user installs from Shopify admin and app loads with just ?shop=
  useEffect(() => {
    const shopFromUrl = searchParams.get("shop");
    const hasToken = searchParams.get("pending_token") || sessionStorage.getItem("pending_shopify_token");
    const alreadyTriggeredOAuth = sessionStorage.getItem("oauth_triggered");
    
    // If we have shop but no token, trigger OAuth to get the access token
    if (shopFromUrl && !hasToken && !alreadyTriggeredOAuth && !user) {
      console.log("Shopify install detected without token, triggering OAuth for:", shopFromUrl);
      sessionStorage.setItem("oauth_triggered", "true");
      
      // Redirect to OAuth init to get proper access token
      const oauthUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-oauth-init?shop=${encodeURIComponent(shopFromUrl)}`;
      window.location.href = oauthUrl;
    }
  }, [searchParams, user]);
  
  // Clear OAuth triggered flag when we have the token
  useEffect(() => {
    if (pendingShopifyToken) {
      sessionStorage.removeItem("oauth_triggered");
    }
  }, [pendingShopifyToken]);
  
  // Store pending Shopify data in sessionStorage for use after login
  useEffect(() => {
    if (pendingShopifyShop && pendingShopifyToken) {
      sessionStorage.setItem("pending_shopify_shop", pendingShopifyShop);
      sessionStorage.setItem("pending_shopify_token", pendingShopifyToken);
      if (pendingShopName) {
        sessionStorage.setItem("pending_shopify_name", pendingShopName);
      }
    }
  }, [pendingShopifyShop, pendingShopifyToken, pendingShopName]);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  
  // Signup form state - auto-fill email from Shopify if available
  const [signupEmail, setSignupEmail] = useState(pendingShopEmail || "");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [storeName, setStoreName] = useState(pendingShopName || "");
  const [storeDomain, setStoreDomain] = useState(pendingShopifyShop || "");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  
  // Platform selection state (for business only)
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformType | null>(
    pendingShopifyShop || pendingShopifyToken ? "shopify" : null
  );
  const [signupStep, setSignupStep] = useState<"platform" | "details">(
    pendingShopifyShop || pendingShopifyToken ? "details" : "platform"
  );
  
  // Form validation
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [authTab, setAuthTab] = useState<"login" | "signup">(() => {
    // For Shopify installs, always default to signup
    if (pendingShopifyShop || pendingShopifyToken || searchParams.get("tab") === "signup") {
      return "signup";
    }
    return "login";
  });
  
  // Update form fields and tab when Shopify data is available from URL
  useEffect(() => {
    // Auto-switch to signup tab for Shopify installs
    if (pendingShopifyShop || pendingShopifyToken) {
      setAuthTab("signup");
      setUserType("business");
      setSelectedPlatform("shopify");
      setSignupStep("details"); // Skip platform selection
    }
    if (pendingShopifyShop && !storeDomain) {
      setStoreDomain(pendingShopifyShop);
    }
    if (pendingShopName && !storeName) {
      setStoreName(pendingShopName);
    }
    if (pendingShopEmail && !signupEmail) {
      setSignupEmail(pendingShopEmail);
    }
  }, [pendingShopifyShop, pendingShopifyToken, pendingShopName, pendingShopEmail, storeDomain, storeName, signupEmail]);

  // Customer OTP verification state
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerOtp, setCustomerOtp] = useState("");
  const [customerStep, setCustomerStep] = useState<"email" | "otp">("email");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);

  // Check for existing customer session
  useEffect(() => {
    if (userType === "customer") {
      const storedSession = sessionStorage.getItem(SESSION_KEY);
      if (storedSession) {
        try {
          const session = JSON.parse(storedSession);
          if (session.email) {
            // Already verified, redirect to dashboard
            navigate("/my-purchases", { replace: true });
            return;
          }
        } catch {
          sessionStorage.removeItem(SESSION_KEY);
        }
      }
    }
  }, [userType, navigate]);

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    if (!signupPassword) return { score: 0, label: "", color: "" };
    
    let score = 0;
    if (signupPassword.length >= 6) score += 1;
    if (signupPassword.length >= 8) score += 1;
    if (/[A-Z]/.test(signupPassword)) score += 1;
    if (/[0-9]/.test(signupPassword)) score += 1;
    if (/[^A-Za-z0-9]/.test(signupPassword)) score += 1;
    
    if (score <= 1) return { score: 20, label: "Weak", color: "bg-red-500" };
    if (score <= 2) return { score: 40, label: "Fair", color: "bg-orange-500" };
    if (score <= 3) return { score: 60, label: "Good", color: "bg-yellow-500" };
    if (score <= 4) return { score: 80, label: "Strong", color: "bg-green-500" };
    return { score: 100, label: "Excellent", color: "bg-green-600" };
  }, [signupPassword]);

  // Validation helpers
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidShopifyDomain = (domain: string) => /^[a-zA-Z0-9-]+\.myshopify\.com$/.test(domain);
  const passwordsMatch = signupPassword === confirmPassword;

  // Reset form when switching user types (but not for Shopify installs)
  useEffect(() => {
    // Don't reset if this is a Shopify install flow
    const isShopifyInstallFlow = pendingShopifyShop || pendingShopifyToken;
    if (!isShopifyInstallFlow) {
      setSignupStep("platform");
      setSelectedPlatform(null);
    }
    setTouched({});
    // Reset customer OTP state
    setCustomerEmail("");
    setCustomerOtp("");
    setCustomerStep("email");
    setOtpError(null);
  }, [userType, pendingShopifyShop, pendingShopifyToken]);

  // Customer OTP handlers
  const handleSendCustomerOtp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!customerEmail.trim() || !isValidEmail(customerEmail)) {
      setOtpError("Please enter a valid email address");
      return;
    }

    setIsSendingOtp(true);
    setOtpError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-gift-verification-otp", {
        body: { 
          email: customerEmail.trim().toLowerCase(),
          method: "email"
        },
      });

      if (fnError) throw fnError;

      toast.success("Verification code sent! Check your email.");
      setCustomerStep("otp");
    } catch (err: any) {
      console.error("Error sending OTP:", err);
      setOtpError(err.message || "Failed to send verification code");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyCustomerOtp = async () => {
    if (customerOtp.length !== 6) {
      setOtpError("Please enter the 6-digit code");
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("verify-gift-otp", {
        body: { 
          email: customerEmail.trim().toLowerCase(),
          code: customerOtp,
          method: "email"
        },
      });

      if (fnError) throw fnError;

      if (data?.error) {
        setOtpError(data.error);
        setIsVerifyingOtp(false);
        return;
      }

      // Store session
      const session = {
        email: data.email,
        phone: data.phone,
        method: "email",
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));

      toast.success("Verified! Redirecting to your dashboard...");
      navigate("/my-purchases", { replace: true });
    } catch (err: any) {
      console.error("Error verifying OTP:", err);
      setOtpError(err.message || "Failed to verify code");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    const { error } = await signIn(loginEmail, loginPassword);
    
    if (error) {
      setLoading(false);
      toast.error(error.message);
    } else {
      // Set remember me preference
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberMe");
        sessionStorage.setItem("sessionActive", "true");
      }
      
      // Check for pending Shopify connection after login
      const storedShop = sessionStorage.getItem("pending_shopify_shop");
      const storedToken = sessionStorage.getItem("pending_shopify_token");
      const storedShopName = sessionStorage.getItem("pending_shopify_name");
      
      if (storedShop && storedToken) {
        try {
          // Get user's merchant ID
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser) {
            const { data: merchantUser } = await supabase
              .from("merchant_users")
              .select("merchant_id")
              .eq("user_id", authUser.id)
              .maybeSingle();
            
            if (merchantUser?.merchant_id) {
              // Update existing merchant with Shopify credentials
              await supabase
                .from("merchants")
                .update({
                  shopify_store_domain: storedShop,
                  shopify_access_token: storedToken,
                  store_name: storedShopName || storedShop.replace(".myshopify.com", ""),
                  platform_type: "shopify",
                })
                .eq("id", merchantUser.merchant_id);
              
              toast.success("Shopify store connected successfully!");
            } else {
              // User exists but has no merchant - use SECURITY DEFINER function
              console.log("Creating new merchant for existing user with Shopify store");
              const { error: merchantError } = await supabase
                .rpc('create_merchant_with_user', {
                  p_user_id: authUser.id,
                  p_store_domain: storedShop,
                  p_store_name: storedShopName || storedShop.replace(".myshopify.com", ""),
                  p_platform_type: "shopify",
                  p_access_token: storedToken,
                  p_widget_installed: true,
                });

              if (!merchantError) {
                toast.success("Shopify store connected successfully!");
              } else {
                console.error("Error creating merchant:", merchantError);
                toast.error("Failed to connect Shopify store. Please try again.");
              }
            }
          }
          
          // Clear pending data and redirect to merchant onboarding
          sessionStorage.removeItem("pending_shopify_shop");
          sessionStorage.removeItem("pending_shopify_token");
          sessionStorage.removeItem("pending_shopify_name");
          
          toast.success("Welcome back! Let's complete your store setup.");
          setBypassBlocker(true);
          await new Promise(resolve => setTimeout(resolve, 300));
          navigate("/merchant/onboarding", { replace: true });
          setTimeout(() => setBypassBlocker(false), 100);
          setLoading(false);
          return; // Exit early - we've handled the Shopify flow
        } catch (err) {
          console.error("Error linking Shopify store:", err);
          // Don't block login, just log the error
        }
      }
      
      toast.success("Welcome back!");
      // Bypass navigation blocker for redirect
      setBypassBlocker(true);
      
      // Small delay to ensure auth state is fully propagated before navigation
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const redirectPath = searchParams.get("redirect") || "/merchant-dashboard";
      navigate(redirectPath, { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      // Store user type preference for post-OAuth handling (both storage for reliability)
      sessionStorage.setItem("oauth_user_type", userType);
      localStorage.setItem("oauth_user_type", userType);
      
      const { error } = await signInWithGoogle();
      if (error) {
        toast.error(error.message || "Failed to sign in with Google");
        setGoogleLoading(false);
      }
      // If successful, user will be redirected to Google
    } catch (err: any) {
      console.error("Google sign-in error:", err);
      toast.error("Failed to initiate Google sign-in");
      setGoogleLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setTouched({ email: true, password: true, confirm: true, store: true, domain: true });
    
    const isShopifyPlatform = selectedPlatform === "shopify";
    
    // For professionals, business name is optional
    const isBusinessNameRequired = userType !== "professional";
    if (!signupEmail || !signupPassword || !confirmPassword || (isBusinessNameRequired && !storeName)) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (userType === "business" && isShopifyPlatform && !storeDomain) {
      toast.error("Please enter your Shopify store domain");
      return;
    }
    
    if (!isValidEmail(signupEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    if (!passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (signupPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (userType === "business" && isShopifyPlatform && !isValidShopifyDomain(storeDomain)) {
      toast.error("Please enter a valid Shopify store domain (e.g., your-store.myshopify.com)");
      return;
    }
    
    setLoading(true);
    
    try {
      if (userType === "business" && isShopifyPlatform) {
        const { data: existingMerchant } = await supabase
          .from('merchants')
          .select('id')
          .eq('shopify_store_domain', storeDomain)
          .maybeSingle();

        if (existingMerchant) {
          toast.error("This store is already registered. Please login or contact support.");
          setLoading(false);
          return;
        }
      }

      const { error: signUpError, data: authData } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          emailRedirectTo: window.location.origin,
        }
      });
      
      if (signUpError) {
        toast.error(signUpError.message);
        setLoading(false);
        return;
      }

      if (!authData.user || !authData.session) {
        toast.error("Failed to create account - please try again");
        setLoading(false);
        return;
      }

      // No need to wait for session - using SECURITY DEFINER RPC function

      // Check for pending Shopify data from OAuth flow (takes precedence)
      const pendingShop = sessionStorage.getItem("pending_shopify_shop");
      const pendingToken = sessionStorage.getItem("pending_shopify_token");
      const pendingName = sessionStorage.getItem("pending_shopify_name");
      
      // Determine if this is a Shopify install flow
      const isShopifyInstallFlow = pendingShop && pendingToken;
      
      // Determine platform type - Shopify OAuth data takes precedence
      let platformType: string;
      let domainToUse: string;
      let storeNameToUse: string;
      let accessTokenToUse: string | null = null;
      
      if (isShopifyInstallFlow) {
        // Use pending Shopify data from OAuth
        platformType = "shopify";
        domainToUse = pendingShop;
        storeNameToUse = pendingName || pendingShop.replace(".myshopify.com", "");
        accessTokenToUse = pendingToken;
        console.log("Using pending Shopify data for signup:", pendingShop);
      } else if (userType === "professional") {
        platformType = "standalone";
        const nameForDomain = storeName || signupEmail.split('@')[0];
        domainToUse = `${nameForDomain.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now()}.professional.shakatah.com`;
        storeNameToUse = storeName || signupEmail.split('@')[0];
      } else if (selectedPlatform === "shopify") {
        platformType = "shopify";
        domainToUse = storeDomain;
        storeNameToUse = storeName || signupEmail.split('@')[0];
      } else {
        platformType = selectedPlatform || "standalone";
        const nameForDomain = storeName || signupEmail.split('@')[0];
        domainToUse = `${nameForDomain.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${Date.now()}.business.shakatah.com`;
        storeNameToUse = storeName || signupEmail.split('@')[0];
      }
      
      // Use SECURITY DEFINER function to create merchant + link user atomically
      // This bypasses the RLS timing issue after signup
      const { data: merchantId, error: merchantError } = await supabase
        .rpc('create_merchant_with_user', {
          p_user_id: authData.user.id,
          p_store_domain: domainToUse,
          p_store_name: storeNameToUse,
          p_platform_type: platformType,
          p_access_token: accessTokenToUse,
          p_widget_installed: !!accessTokenToUse,
        });

      if (merchantError) {
        console.error("Error creating merchant:", merchantError);
        toast.error("Account created but setup failed. Please login to complete setup.");
        navigate("/merchant-login", { replace: true });
        setLoading(false);
        return;
      }

      // Set session persistence flags (same as login)
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberMe");
        sessionStorage.setItem("sessionActive", "true");
      }

      // Clear pending Shopify data if it was used
      if (isShopifyInstallFlow) {
        sessionStorage.removeItem("pending_shopify_shop");
        sessionStorage.removeItem("pending_shopify_token");
        sessionStorage.removeItem("pending_shopify_name");
      }

      toast.success("Account created! Let's set up your profile.");
      // Bypass navigation blocker for redirect to onboarding
      setBypassBlocker(true);
      
      // Redirect to appropriate onboarding based on actual platform type (not just user type selection)
      // If Shopify install, always go to merchant onboarding
      let defaultRedirect: string;
      if (isShopifyInstallFlow || platformType === "shopify") {
        defaultRedirect = "/merchant/onboarding";
      } else if (userType === "professional") {
        defaultRedirect = "/professional/onboarding";
      } else {
        defaultRedirect = "/merchant/onboarding";
      }
      
      const redirectUrl = searchParams.get("redirect") || defaultRedirect;
      navigate(redirectUrl, { replace: true });
      // Reset bypass flag after navigation
      setTimeout(() => setBypassBlocker(false), 100);
    } catch (error) {
      console.error("Signup error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Benefits per user type
  const benefits = {
    business: [
      { icon: <TrendingUp className="h-5 w-5" />, title: "Boost Average Order Value", description: "40% higher AOV with group purchases for birthdays & weddings" },
      { icon: <Zap className="h-5 w-5" />, title: "5-Minute Setup", description: "Install widget, configure settings, start accepting group payments" },
      { icon: <Shield className="h-5 w-5" />, title: "Inventory Protection", description: "Automatic reservation when a group purchase is initiated" },
      { icon: <BarChart3 className="h-5 w-5" />, title: "Real-time Dashboard", description: "Track orders, payments, and revenue in one place" },
    ],
    professional: [
      { icon: <Wallet className="h-5 w-5" />, title: "Collect Group Payments", description: "Split bills among clients for events, projects, or services" },
      { icon: <Calendar className="h-5 w-5" />, title: "Event Management", description: "Organize group purchases for weddings, parties, and corporate events" },
      { icon: <Users className="h-5 w-5" />, title: "Client Coordination", description: "Invite participants and track contributions in real-time" },
      { icon: <BarChart3 className="h-5 w-5" />, title: "Financial Reports", description: "Generate invoices and track payment history" },
    ],
    customer: [
      { icon: <Heart className="h-5 w-5" />, title: "Track Your Gifts", description: "See all group gifts you've organized or contributed to" },
      { icon: <Users className="h-5 w-5" />, title: "Coordinate with Friends", description: "Split costs easily with group discussion features" },
      { icon: <Gift className="h-5 w-5" />, title: "Special Occasions", description: "Never miss a birthday, wedding, or celebration" },
      { icon: <Shield className="h-5 w-5" />, title: "Secure Payments", description: "Your contributions are protected and tracked" },
    ],
  };

  const userTypeConfig = {
    business: {
      icon: <Store className="h-5 w-5" />,
      title: "Business Portal",
      subtitle: "For stores & e-commerce",
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    professional: {
      icon: <Briefcase className="h-5 w-5" />,
      title: "Professional Portal",
      color: "text-cyan-600",
      bgColor: "bg-cyan-100 dark:bg-cyan-950/30",
    },
    customer: {
      icon: <User className="h-5 w-5" />,
      title: "Customer Portal",
      subtitle: "Track your purchases & gifts",
      color: "text-pink-600",
      bgColor: "bg-pink-100 dark:bg-pink-950/30",
    },
  };

  const currentConfig = userTypeConfig[userType];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/30">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg text-foreground">
            <Briefcase className="w-5 h-5 text-cyan-600" />
            Pro@Shakatah
          </Link>
          <nav className="flex items-center gap-4">
            <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Support
            </Link>
          </nav>
        </div>
      </header>

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-4 md:py-6">
        {/* Professional badge */}
        <div className="flex justify-center mb-6">
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start max-w-6xl mx-auto">
          {/* Left side - Benefits */}
          <div className="hidden lg:block">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-100 dark:bg-cyan-950/30 text-cyan-600 mb-3">
                <Briefcase className="h-6 w-6" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">
                Manage Group Payments
              </h1>
              <p className="text-base text-muted-foreground">
                Collect group payments from clients for events and services
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-2.5 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300 text-xs font-medium">Event Planners</span>
              <span className="px-2.5 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300 text-xs font-medium">Freelancers</span>
              <span className="px-2.5 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300 text-xs font-medium">Agencies</span>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground text-sm">
                Why professionals choose Pro@Shakatah:
              </h3>
              {benefits.professional.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-card border border-border">
                  <div className="p-1.5 rounded-md bg-cyan-100 dark:bg-cyan-950/30 text-cyan-600">
                    {benefit.icon}
                  </div>
                  <div>
                    <h4 className="font-medium text-sm">{benefit.title}</h4>
                    <p className="text-xs text-muted-foreground">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Login Form */}
          <Card className="w-full max-w-md mx-auto lg:mx-0">
            <CardHeader className="text-center pb-2 pt-4">
              <div className="mx-auto w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-cyan-100 dark:bg-cyan-950/30">
                <span className="text-cyan-600">
                  <Briefcase className="h-5 w-5" />
                </span>
              </div>
              <CardTitle className="text-xl">Professional Portal</CardTitle>
            </CardHeader>
            {userType === "customer" ? (
              // Customer portal - OTP-based verification
              <CardContent className="space-y-4 pt-2 pb-4">
                {customerStep === "email" ? (
                  <form onSubmit={handleSendCustomerOtp} className="space-y-4">
            
                    <p className="text-sm text-muted-foreground text-center">
                      Enter your email to access your gift and group purchase dashboard.
                    </p>
                    
                    <div className="space-y-2">
                      <Label htmlFor="customer-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="customer-email"
                          type="email"
                          placeholder="you@example.com"
                          className="pl-10"
                          value={customerEmail}
                          onChange={(e) => {
                            setCustomerEmail(e.target.value);
                            setOtpError(null);
                          }}
                          disabled={isSendingOtp}
                          autoComplete="email"
                        />
                      </div>
                      {otpError && (
                        <p className="text-xs text-destructive">{otpError}</p>
                      )}
                    </div>

                    <Button 
                      type="submit"
                      className="w-full bg-pink-600 hover:bg-pink-700" 
                      disabled={isSendingOtp || !customerEmail.trim()}
                    >
                      {isSendingOtp ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending code...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Send Verification Code
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-muted-foreground text-center">
                      We'll send a 6-digit code to verify your email
                    </p>

                    {/* Google Sign-in Divider */}
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">or</span>
                      </div>
                    </div>

                    <Button 
                      type="button"
                      variant="outline" 
                      className="w-full" 
                      onClick={handleGoogleSignIn}
                      disabled={googleLoading || isSendingOtp}
                    >
                      {googleLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      )}
                      Continue with Google
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Enter the 6-digit code sent to
                      </p>
                      <p className="font-medium text-foreground">{customerEmail}</p>
                    </div>
                    
                    <div className="flex justify-center">
                      <InputOTP 
                        maxLength={6} 
                        value={customerOtp}
                        onChange={(value) => {
                          setCustomerOtp(value);
                          setOtpError(null);
                        }}
                        disabled={isVerifyingOtp}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    
                    {otpError && (
                      <p className="text-xs text-destructive text-center">{otpError}</p>
                    )}

                    <Button 
                      className="w-full bg-pink-600 hover:bg-pink-700" 
                      onClick={handleVerifyCustomerOtp}
                      disabled={isVerifyingOtp || customerOtp.length !== 6}
                    >
                      {isVerifyingOtp ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <Check className="mr-2 h-4 w-4" />
                          Verify & Continue
                        </>
                      )}
                    </Button>
                    
                    <div className="flex items-center justify-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setCustomerStep("email");
                          setCustomerOtp("");
                          setOtpError(null);
                        }}
                        disabled={isVerifyingOtp}
                      >
                        <ArrowLeft className="mr-1 h-3 w-3" />
                        Change email
                      </Button>
                      <span className="text-muted-foreground">|</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={handleSendCustomerOtp}
                        disabled={isSendingOtp || isVerifyingOtp}
                      >
                        {isSendingOtp ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : null}
                        Resend code
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            ) : (
              // Business & Professional - standard login/signup
              <Tabs value={authTab} onValueChange={(v) => setAuthTab(v as "login" | "signup")} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mx-6" style={{ width: 'calc(100% - 48px)' }}>
                  <TabsTrigger value="login">Login</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin}>
                    <CardContent className="space-y-3 pt-2 pb-3">
                      <div className="space-y-2">
                        <Label htmlFor="login-email">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="login-email"
                            type="email"
                            placeholder={userType === "professional" ? "you@example.com" : "store@example.com"}
                            className="pl-10"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            disabled={loading}
                            autoComplete="email"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="login-password">Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="login-password"
                            type={showLoginPassword ? "text" : "password"}
                            placeholder="••••••••"
                            className="pl-10 pr-10"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            disabled={loading}
                            autoComplete="current-password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                            onClick={() => setShowLoginPassword(!showLoginPassword)}
                            tabIndex={-1}
                          >
                            {showLoginPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="remember-me" 
                            checked={rememberMe}
                            onCheckedChange={(checked) => setRememberMe(checked === true)}
                          />
                          <Label 
                            htmlFor="remember-me" 
                            className="text-sm font-normal text-muted-foreground cursor-pointer"
                          >
                            Remember me
                          </Label>
                        </div>
                        <Button
                          type="button"
                          variant="link"
                          className="px-0 text-sm text-muted-foreground hover:text-primary"
                          onClick={async () => {
                            if (!loginEmail) {
                              toast.error("Please enter your email address first");
                              return;
                            }
                            if (!isValidEmail(loginEmail)) {
                              toast.error("Please enter a valid email address");
                              return;
                            }
                            setLoading(true);
                            try {
                              // Use custom edge function for branded password reset email
                              const response = await supabase.functions.invoke('send-password-reset', {
                                body: { 
                                  email: loginEmail,
                                  redirectTo: `${window.location.origin}/reset-password`
                                }
                              });
                              
                              if (response.error) {
                                throw response.error;
                              }
                              
                              toast.success("Password reset email sent! Check your inbox.");
                            } catch (error: any) {
                              console.error("Password reset error:", error);
                              toast.error("Failed to send reset email. Please try again.");
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={loading}
                        >
                          Forgot password?
                        </Button>
                      </div>
                    </CardContent>
                    <CardFooter className="pt-2 pb-4">
                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                        size="sm" 
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          "Sign In"
                        )}
                      </Button>

                      {userType === "professional" && (
                        <>
                          <div className="relative my-2">
                            <div className="absolute inset-0 flex items-center">
                              <span className="w-full border-t border-border" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-card px-2 text-muted-foreground">or</span>
                            </div>
                          </div>
                          <Button 
                            type="button"
                            variant="outline" 
                            className="w-full" 
                            onClick={handleGoogleSignIn}
                            disabled={googleLoading || loading}
                          >
                            {googleLoading ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                            )}
                            Continue with Google
                          </Button>
                        </>
                      )}
                    </CardFooter>
                  </form>
                </TabsContent>
                
                <TabsContent value="signup">
                  {userType === "business" && signupStep === "platform" ? (
                    <div>
                      <CardContent className="space-y-4">
                        <div className="text-center mb-2">
                          <p className="text-sm text-muted-foreground">
                            Choose how you want to integrate Shakatah
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground text-center">Platform selection has been simplified. Professional accounts are created automatically.</p>
                      </CardContent>
                      <CardFooter>
                        <Button 
                          type="button" 
                          className="w-full" 
                          disabled={!selectedPlatform}
                          onClick={() => setSignupStep("details")}
                        >
                          Continue
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </CardFooter>
                    </div>
                  ) : (
                    <form onSubmit={handleSignup}>
                      <CardContent className="space-y-4">
                        {userType === "business" && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setSignupStep("platform")}
                            className="p-0 h-auto text-muted-foreground hover:text-foreground"
                          >
                            <ArrowLeft className="mr-1 h-3 w-3" />
                            Back to platform selection
                          </Button>
                        )}
                        
                        {/* Type badge */}
                        <div className={`flex items-center gap-2 p-2 rounded-lg ${currentConfig.bgColor}`}>
                          <span className={currentConfig.color}>{currentConfig.icon}</span>
                          <span className="text-sm font-medium">
                            {userType === "business" 
                              ? (selectedPlatform === "shopify" ? "Shopify Store" : selectedPlatform === "standalone" ? "Standalone Store" : "API Integration")
                              : "Professional Account"
                            }
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="store-name">
                            {userType === "professional" ? "Business Name (optional)" : "Store Name"}
                          </Label>
                          <div className="relative">
                            {userType === "professional" ? (
                              <Briefcase className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Store className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            )}
                            <Input
                              id="store-name"
                              type="text"
                              placeholder={userType === "professional" ? "My Event Planning Co" : "My Awesome Store"}
                              className="pl-10"
                              value={storeName}
                              onChange={(e) => setStoreName(e.target.value)}
                              onBlur={() => setTouched(prev => ({ ...prev, store: true }))}
                              disabled={loading}
                            />
                          </div>
                        </div>
                        
                        {userType === "business" && selectedPlatform === "shopify" && (
                          <div className="space-y-2">
                            <Label htmlFor="store-domain">Shopify Store Domain</Label>
                            <div className="relative">
                              <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input
                                id="store-domain"
                                type="text"
                                placeholder="your-store.myshopify.com"
                                className={`pl-10 pr-10 ${touched.domain && storeDomain && !isValidShopifyDomain(storeDomain) ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                value={storeDomain}
                                onChange={(e) => setStoreDomain(e.target.value.toLowerCase())}
                                onBlur={() => setTouched(prev => ({ ...prev, domain: true }))}
                                disabled={loading || !!pendingShopifyShop}
                              />
                              {touched.domain && storeDomain && (
                                <div className="absolute right-3 top-3">
                                  {isValidShopifyDomain(storeDomain) ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <X className="h-4 w-4 text-destructive" />
                                  )}
                                </div>
                              )}
                            </div>
                            {pendingShopifyShop ? (
                              <p className="text-xs text-green-600">
                                ✓ Auto-filled from Shopify app install
                              </p>
                            ) : touched.domain && storeDomain && !isValidShopifyDomain(storeDomain) ? (
                              <p className="text-xs text-destructive">
                                Must be in format: your-store.myshopify.com
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                Enter your Shopify permanent domain
                              </p>
                            )}
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          <Label htmlFor="signup-email">Email</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="signup-email"
                              type="email"
                              placeholder={userType === "professional" ? "you@example.com" : "store@example.com"}
                              className={`pl-10 pr-10 ${touched.email && signupEmail && !isValidEmail(signupEmail) ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                              value={signupEmail}
                              onChange={(e) => setSignupEmail(e.target.value)}
                              onBlur={() => setTouched(prev => ({ ...prev, email: true }))}
                              disabled={loading}
                              autoComplete="email"
                            />
                            {touched.email && signupEmail && (
                              <div className="absolute right-3 top-3">
                                {isValidEmail(signupEmail) ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <X className="h-4 w-4 text-destructive" />
                                )}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            This email will receive notifications for all activities
                          </p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="signup-password">Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="signup-password"
                              type={showSignupPassword ? "text" : "password"}
                              placeholder="••••••••"
                              className="pl-10 pr-10"
                              value={signupPassword}
                              onChange={(e) => setSignupPassword(e.target.value)}
                              onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
                              disabled={loading}
                              autoComplete="new-password"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                              onClick={() => setShowSignupPassword(!showSignupPassword)}
                              tabIndex={-1}
                            >
                              {showSignupPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                            </Button>
                          </div>
                          {signupPassword && (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Progress value={passwordStrength.score} className={`h-1.5 flex-1 ${passwordStrength.color}`} />
                                <span className={`text-xs font-medium ${passwordStrength.score >= 60 ? 'text-green-600' : passwordStrength.score >= 40 ? 'text-yellow-600' : 'text-destructive'}`}>
                                  {passwordStrength.label}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Use 8+ characters with uppercase, numbers, and symbols
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="confirm-password">Confirm Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              id="confirm-password"
                              type={showSignupPassword ? "text" : "password"}
                              placeholder="••••••••"
                              className={`pl-10 pr-10 ${touched.confirm && confirmPassword && !passwordsMatch ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              onBlur={() => setTouched(prev => ({ ...prev, confirm: true }))}
                              disabled={loading}
                              autoComplete="new-password"
                            />
                            {touched.confirm && confirmPassword && (
                              <div className="absolute right-3 top-3">
                                {passwordsMatch ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <X className="h-4 w-4 text-destructive" />
                                )}
                              </div>
                            )}
                          </div>
                          {touched.confirm && confirmPassword && !passwordsMatch && (
                            <p className="text-xs text-destructive">Passwords do not match</p>
                          )}
                        </div>
                      </CardContent>
                      <CardFooter className="flex flex-col gap-3">
                        <Button 
                          type="submit" 
                          className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                          disabled={loading || googleLoading}
                        >
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Creating account...
                            </>
                          ) : (
                            "Create Account"
                          )}
                        </Button>

                        {userType === "professional" && (
                          <>
                            <div className="relative">
                              <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-border" />
                              </div>
                              <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-card px-2 text-muted-foreground">or</span>
                              </div>
                            </div>
                            <Button 
                              type="button"
                              variant="outline" 
                              className="w-full" 
                              onClick={handleGoogleSignIn}
                              disabled={googleLoading || loading}
                            >
                              {googleLoading ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                              )}
                              Continue with Google
                            </Button>
                          </>
                        )}
                      </CardFooter>
                    </form>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default UnifiedLogin;
